import { useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  useQdsSignaturesSelector,
  useFetchedQdsDocumentSelector,
} from "../../../store/qdsDocuments";
import { qdsDocumentsActions } from "../../../store/qdsDocuments/QdsDocumentReducer";
import { QdsSignature } from "../../../store/qdsDocuments/QdsDocumentReducer";
import { usePDF } from "../../../modules/pdf";
import { useToast } from "../../../components/toast";
import DigitalSignatureQualifiedGateway from "../../../api/gateways/DigitalSignatureQualifiedGateway";
import { useTranslation } from "react-i18next";
import { QdsBlobCache } from "../utils/QdsBlobCache";
import { extractQueryParamValue } from "../utils/QueryParams";

export const useHandleQdsSignatures = () => {
  const dispatch = useDispatch();
  const signatures = useQdsSignaturesSelector();
  const { applySignaturesToPdf } = usePDF();
  const fetchedDocumentData = useFetchedQdsDocumentSelector();
  const { danger, success } = useToast();
  const { t } = useTranslation();

  const handleStartSigning = () => {
    dispatch(qdsDocumentsActions.setIsQdsSigning(true));
  };

  const addSignature = useCallback(
    (signature: Omit<QdsSignature, "id">) => {
      const id = Date.now();
      dispatch(qdsDocumentsActions.addQdsSignature({ ...signature, id }));
    },
    [dispatch]
  );

  const removeSignature = useCallback(
    (id: number) => {
      const confirm = window.confirm("Are you sure you want to delete this signature?");
      if (confirm) {
        dispatch(qdsDocumentsActions.removeQdsSignature(id));
      }
    },
    [dispatch]
  );

  const clearSignatures = useCallback(() => {
    dispatch(qdsDocumentsActions.clearQdsSignatures());
  }, [dispatch]);

  const handleCancelSigning = () => {
    dispatch(qdsDocumentsActions.setIsQdsSigning(false));
    clearSignatures();
  };

  const handleSubmitSigning = async () => {
    if (signatures.length === 0) {
      danger(t("ERRORS.INVALID_SIGNED_DOCUMENT"));

      return;
    }

    const blob = QdsBlobCache.get();
    const lastFileIndex = (fetchedDocumentData?.document?.files?.length || 0) - 1;
    const fileData = fetchedDocumentData?.document?.files?.[lastFileIndex];

    if (!fileData || !blob) {
      danger(t("ERRORS.NO_FILE"));
      return;
    }

    try {
      const file = new File([blob], fileData?.name, {
        type: "application/pdf",
      });

      // Apply signatures and get the signed PDF blob
      const signedFile = await applySignaturesToPdf(file, signatures);

      if (!signedFile) {
        danger(t("ERRORS.EMBED_SIGNATURES"));
        return;
      }

      const token = extractQueryParamValue("token");
      const email = extractQueryParamValue("email");

      if (!token || !email) {
        danger(t("ERRORS.MISSING_TOKEN_OR_EMAIL"));
        return;
      }

      // Upload the signed PDF to the backend
      const formData = new FormData();
      formData.append("file", signedFile);
      formData.append("email", email);
      formData.append("token", token);

      // Send to backend
      const result = await DigitalSignatureQualifiedGateway.submitWebQdsSignedDocument(formData);

      if ("status" in result && "id" in result) {
        // Notify other tabs that QDS signature was updated
        const qdsRequestId = fetchedDocumentData?.qdsRequest?.id;
        if (qdsRequestId) {
          const channel = new BroadcastChannel('qds_signature_update');
          channel.postMessage({ qdsRequestId });
          channel.close();
        }

        success(t("WEB_QDS_SIGNATURE.SUBMITTED_MESSAGE"));

        dispatch(qdsDocumentsActions.setIsQdsSigning(false));
        dispatch(qdsDocumentsActions.setQdsDocumentSuccessfullySignedStatus(true));
        clearSignatures();
      } else {
        danger(t("ERRORS.SIGNATURE_SUBMISSION_FAILED"));
      }
    } catch (error) {
      danger(t("ERRORS.UNKNOWN"));
    }
  };

  return {
    signatures,
    addSignature,
    removeSignature,
    clearSignatures,
    handleCancelSigning,
    handleSubmitSigning,
    handleStartSigning,
  };
};
